import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import { bindMediaAudioFocus, previewVoice } from '@orborix/voice-client';
import { useHoldGesture, type HoldRecorder } from '@orborix/voice-composer';
import { assistedVisibleWords } from '@orborix/shared-types';
import { play as playCelebration } from '../../../lib/celebrations/index.js';
import { sfx } from '../../../lib/sfx/index.js';
import { IconCheck, IconMicSolid, IconRefresh, IconSpeaker } from '../icons.js';
import { ASSISTED_COPY } from './assistedCopy.js';
import { rejectionText } from './assistedModel.js';
import type { UseAssistedTurnReturn } from './useAssistedTurn.js';
import './assisted.css';

/** La celebración de la app dura ~1,1 s; si el pulso no corre (pestaña oculta), seguimos igual. */
const CELEBRATION_FALLBACK_MS = 1600;
/** Cuándo sale volando la frase hacia tu burbuja (ya está en la charla para entonces). */
const FLY_AFTER_MS = 450;
const FLY_MS = 600;
/** El desvanecido de la charla de arriba (`assisted.css`, `--asst-fade`): hasta dónde llega. */
const FADE_PX = 110;

/**
 * La frase dicha bien sube volando hasta tu burbuja de la charla (maqueta 1):
 * una copia que va de la frase de la hoja a la última burbuja tuya. Sin burbuja
 * a la vista (o sin animaciones), no vuela: es sólo un adorno.
 */
function flyToBubble(deck: HTMLElement | null): void {
  const from = deck?.querySelector<HTMLElement>('.asst-dk-phrase');
  const bubbles = document.querySelectorAll<HTMLElement>('.card.user .body');
  const to = bubbles[bubbles.length - 1];
  if (!from || !to || typeof from.animate !== 'function') return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  if (!a.width || !b.width || b.bottom < 0) return;
  const c = document.createElement('div');
  c.className = 'asst-fly';
  c.textContent = from.textContent;
  Object.assign(c.style, {
    left: `${a.left}px`,
    top: `${a.top}px`,
    width: `${a.width}px`,
  });
  document.body.appendChild(c);
  from.classList.add('is-going');
  const anim = c.animate(
    [
      { transform: 'none', opacity: 1 },
      {
        transform: `translate(${b.left - a.left}px, ${b.top - a.top}px) scale(${b.width / a.width})`,
        opacity: 0.2,
      },
    ],
    { duration: FLY_MS, easing: 'cubic-bezier(0.5, 0, 0.2, 1)', fill: 'forwards' },
  );
  anim.onfinish = () => c.remove();
  anim.oncancel = () => c.remove();
}

interface AssistedCardProps {
  assisted: UseAssistedTurnReturn;
  voiceId: string;
  /**
   * Agacha el mic mientras suena «Escuchar» (`freeChat.setDucked`): en manos
   * libres el mic está abierto y tomaba la frase reproducida como si la dijera
   * el alumno — y el portero la aceptaba.
   */
  onDuck?: (ducked: boolean) => void;
  /**
   * Terminar la escena y ver el cierre (el `leave` del controller). Lo ofrece
   * la oferta del final: el «Terminar» flotante de las escenas queda tapado por
   * la hoja (Emmanuel, 14/10: «la charla asistida no termina nunca»).
   */
  onFinish?: () => void;
  /**
   * El micrófono del turno (el mismo `useHoldTurn` del composer, que en la
   * charla asistida no se muestra): mantener = soltar manda; un toque lo deja
   * grabando y otro toque manda. Sin él (la tarjeta «Ahora solo»), no hay mic.
   */
  recorder?: HoldRecorder;
  holdDisabled?: boolean;
}

/**
 * La hoja de la charla asistida (Emmanuel eligió la maqueta 1 · Escenario con
 * la hoja de la maqueta 2, 16/10; fuente en
 * `docs/design/mejoras/misiones/99-ref/maquetas-asistida/`): va al final de la
 * charla, con su manija, «✦ DECILA TAL CUAL · N» y «Hacelo solo», la frase con
 * su karaoke, la traducción y qué hacer, y abajo Escuchar · el micrófono ·
 * Seguir. Al acertar, el sello y la celebración de la app, y la charla sigue
 * sola; si no sale, qué oyó y «Decila una vez más» (que ya abre el micrófono).
 * También la oferta de hacerlo solo y, después de «Hacelo solo», la tarjeta
 * «Ahora solo» (ésa sobre el composer de siempre).
 */
export function AssistedCard({
  assisted,
  voiceId,
  onDuck,
  onFinish,
  recorder,
  holdDisabled = false,
}: AssistedCardProps) {
  const copy = ASSISTED_COPY.es;
  const { phase, model, result, marks } = assisted;
  const cardRef = useRef<HTMLDivElement>(null);
  /** El micrófono lo abrió «Decila una vez más» y el alumno todavía no lo tocó (card #603). */
  const reopenedRef = useRef(false);
  useDeckFade(cardRef, phase !== 'off');
  const listen = useListen(voiceId, onDuck);
  // Cuando el alumno empieza a hablar, «Escuchar» se calla (pedido de Emmanuel, 14/10).
  const stopListen = listen.stop;
  useEffect(() => {
    if (phase === 'recording') stopListen();
  }, [phase, stopListen]);

  // Acierto: el pulso ✓ de «Confirmación rápida: ejercicio correcto» y los
  // destellos del micro-festejo del A0, con su sonido (los de la app). Cuando
  // termina el pulso, la charla sigue sola (pedido de Emmanuel, 09/10).
  const done = assisted.celebrationDone;
  useEffect(() => {
    if (phase !== 'accepted') return;
    const target = cardRef.current ?? undefined;
    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      done();
    };
    // El sonido de la maqueta 1 (el de «¡Bien dicha!» del repaso).
    sfx.play('game-correct');
    const sparkles = playCelebration('sparkles', {
      ...(target ? { target } : {}),
      count: 14,
      scale: 0.7,
      z: 5,
    });
    const pulse = playCelebration('success-pulse', {
      ...(target ? { target } : {}),
      z: 6,
      onDone: finish,
    });
    const fallback = setTimeout(finish, CELEBRATION_FALLBACK_MS);
    // La frase sube volando hasta tu burbuja (maqueta 1).
    const fly = setTimeout(() => flyToBubble(cardRef.current), FLY_AFTER_MS);
    return () => {
      clearTimeout(fallback);
      clearTimeout(fly);
      sparkles.stop();
      pulse.stop();
    };
  }, [phase, done]);

  // Los sonidos de la maqueta 1 en cada cambio de la hoja (el del acierto, arriba).
  const prevPhaseRef = useRef(phase);
  useEffect(() => {
    const was = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (phase === was) return;
    if (phase === 'rejected') sfx.play('game-miss');
    else if (phase === 'silence') sfx.play('idle-warning');
    else if (phase === 'offer') {
      sfx.play('drill-complete');
      playCelebration('confetti-rain', {});
    }
  }, [phase]);

  if (phase === 'off') {
    if (assisted.soloSince === null) return null;
    return (
      <div className="inspire-card asst-card slide-up" role="status">
        <div className="inspire-card-head">
          <div className="inspire-card-tag">
            <span>{copy.soloNow.tag}</span>
          </div>
        </div>
        <div className="inspire-card-body">
          <div className="inspire-en">{copy.soloNow.title}</div>
          <div className="inspire-es">{copy.soloNow.text}</div>
        </div>
      </div>
    );
  }

  if (phase === 'offer') {
    return (
      <div
        ref={cardRef}
        className="asst-deck is-offer"
        role="region"
        aria-label={copy.offer.title}
      >
        <div className="asst-dk-top">
          <span className="asst-dk-tag">✦ {copy.offer.tag}</span>
        </div>
        <h2 className="asst-offer-title">{copy.offer.title}</h2>
        <p className="asst-offer-text">
          {assisted.offerAllDone ? copy.offer.text : copy.offer.textMain}
        </p>
        {assisted.objectives.length > 0 && (
          <ul className="asst-offer-objs">
            {assisted.objectives.map((o) => (
              <li key={o.id}>
                <IconCheck size={11} />
                <span className="asst-offer-obj">{o.label}</span>
                <span className="asst-offer-help">{copy.offer.withHelp}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="asst-offer-note">{copy.offer.note}</div>
        <div className="asst-offer-btns">
          <button type="button" className="asst-btn" onClick={assisted.restartSolo}>
            {copy.offer.solo}
          </button>
          <div className="asst-offer-row">
            <button type="button" className="asst-btn ghost" onClick={assisted.keepAssisted}>
              {copy.offer.again}
            </button>
            {onFinish && (
              <button type="button" className="asst-btn ghost" onClick={onFinish}>
                {copy.offer.finish}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const soloLink =
    phase === 'recording' || phase === 'judging' || phase === 'accepted' ? null : (
      <button type="button" className="asst-dk-solo" onClick={assisted.goSolo}>
        {copy.card.goSolo}
      </button>
    );

  if (phase === 'error') {
    return (
      <div
        ref={cardRef}
        className="asst-deck is-error"
        role="region"
        aria-label={copy.card.angle}
        onPointerDown={assisted.present}
      >
        <div className="asst-dk-top">
          <span className="asst-dk-tag">✦ {copy.card.tag(assisted.turnNo + 1)}</span>
          {soloLink}
        </div>
        <div className="asst-dk-status">{assisted.error ?? copy.card.error}</div>
        <button type="button" className="asst-btn asst-retry" onClick={assisted.retry}>
          <IconRefresh size={13} />
          <span>{copy.card.retry}</span>
        </button>
      </div>
    );
  }

  // Esperando al coach o armando la frase: la hoja ya está, apagada, con su lugar.
  if (!model || phase === 'intro' || phase === 'waiting' || phase === 'loading') {
    const loading = phase === 'loading';
    return (
      <div
        ref={cardRef}
        className={`asst-deck ${loading ? '' : 'is-idle'}`}
        aria-busy={loading}
        onPointerDown={assisted.present}
      >
        <div className="asst-dk-top">
          <span className="asst-dk-tag">
            ✦ {loading ? copy.card.loadingTag : copy.card.tag(assisted.turnNo + 1)}
          </span>
          {soloLink}
        </div>
        <div className="asst-dk-body">
          <div className="asst-dk-text">
            <span className="inspire-skel inspire-skel-en" />
            <span className="inspire-skel inspire-skel-es" />
          </div>
          <Controls
            recorder={recorder}
            holdDisabled
            active={false}
            failed={false}
            accepted={false}
            playing={false}
            copy={copy}
            onListen={() => {}}
            onFollow={() => {}}
            onRepeat={() => {}}
          />
        </div>
      </div>
    );
  }

  const words = assistedVisibleWords(model.en);
  const accepted = phase === 'accepted';
  const failed = phase === 'rejected' || phase === 'silence';
  const failedMarks = new Set(failed && result ? result.marks : []);
  const acceptedMarks = new Set(accepted && result ? result.marks : []);
  const why = failed && result ? rejectionText(result, copy, model.en) : null;
  const cls = [
    accepted ? 'is-win' : '',
    phase === 'rejected' ? 'is-miss' : '',
    phase === 'recording' ? 'is-rec' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={cardRef}
      className={`asst-deck ${cls}`}
      role="region"
      aria-label={copy.card.angle}
      onPointerDown={assisted.present}
    >
      <div className="asst-dk-top">
        {accepted ? (
          <span className="asst-badge">
            <IconCheck size={13} />
            {copy.card.said}
          </span>
        ) : (
          <span className="asst-dk-tag">✦ {copy.card.tag(assisted.turnNo)}</span>
        )}
        {soloLink}
      </div>
      <div className="asst-dk-body">
        <div className="asst-dk-text">
          <div className="asst-dk-phrase" lang="en">
            {words.map((w, i) => {
              const c = failedMarks.has(i)
                ? 'err-frag err-grammar'
                : accepted
                  ? acceptedMarks.has(i)
                    ? ''
                    : 'asst-heard'
                  : marks[i] === 'heard'
                    ? 'asst-heard'
                    : marks[i] === 'voice'
                      ? 'asst-voice'
                      : '';
              return (
                <span key={i}>
                  {i > 0 ? ' ' : ''}
                  <span className={c}>{w}</span>
                </span>
              );
            })}
          </div>
          <div className="asst-dk-es">{model.es}</div>
          <div className="asst-dk-foot">
            <div className="asst-dk-status" role="status">
              {why ? (
                <>
                  <strong>{why.title}.</strong> {why.text}
                  {why.heard && <span className="asst-dk-heard">{why.heard}</span>}
                </>
              ) : phase === 'recording' ? (
                <>
                  <span className="asst-listening">●</span>{' '}
                  {recorder?.locked ? copy.card.listeningLocked : copy.card.listening}
                </>
              ) : phase === 'judging' ? (
                copy.card.judging
              ) : accepted ? null : (
                copy.card.sayIt
              )}
            </div>
            {/* Secundario, al costado de lo que hay que hacer y lejos del micrófono:
              un toque basta (Emmanuel, 19/10). */}
            {(phase === 'ready' || failed) && (
              <button
                type="button"
                className="asst-dk-discard"
                aria-label={copy.card.discardAria}
                title={copy.card.discardAria}
                onClick={assisted.discard}
              >
                {copy.card.discard}
              </button>
            )}
          </div>
        </div>
        <Controls
          recorder={recorder}
          holdDisabled={holdDisabled}
          active={phase === 'ready' || failed || phase === 'recording'}
          failed={failed}
          accepted={accepted}
          playing={listen.playing}
          copy={copy}
          onListen={() => void listen.play(model.en)}
          onFollow={() => {
            sfx.play('card-flip');
            assisted.follow();
          }}
          reopenedRef={reopenedRef}
          onRepeat={() => {
            assisted.repeat();
            // «Decila una vez más» ya abre el micrófono, como un toque: otro toque manda.
            if (recorder && !holdDisabled) {
              reopenedRef.current = true;
              recorder.start();
              recorder.lock();
            }
          }}
        />
      </div>
    </div>
  );
}

/**
 * Al subir en la charla, la hoja (que es lo último del hilo) se va apagando
 * mientras sale; abajo de todo está entera, y al volver a bajar aparece de nuevo
 * (maqueta 1). `--asst-away` va de 0 a 1 en el 85 % de su alto; apagada, no se
 * toca (`is-away`). Sólo lee el scroll: quién lo mueve sigue siendo del hilo.
 */
function useDeckFade(ref: RefObject<HTMLDivElement | null>, enabled: boolean): void {
  useEffect(() => {
    const deck = ref.current;
    if (!enabled || !deck) return;
    const scroller = deck.closest<HTMLElement>('.app-scroll, .dt-chat');
    if (!scroller) return;
    let raf = 0;
    const update = (): void => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const away = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      const range = Math.max(1, el.offsetHeight * 0.85);
      const p = Math.max(0, Math.min(1, away / range));
      el.style.setProperty('--asst-away', p.toFixed(3));
      el.classList.toggle('is-away', p > 0.6);
      // El desvanecido de arriba es para la charla de antes: termina donde empieza la línea grande
      // del coach, nunca encima (card #577: con la barra de la nave, «Good evening, welcome…» quedaba
      // en la parte desvanecida). Con la línea más abajo, el desvanecido de siempre.
      const featured = scroller.querySelector('.card.coach.is-featured');
      const top = featured
        ? featured.getBoundingClientRect().top - scroller.getBoundingClientRect().top
        : FADE_PX;
      scroller.style.setProperty(
        '--asst-fade',
        `${Math.round(Math.max(0, Math.min(FADE_PX, top)))}px`,
      );
    };
    const schedule = (): void => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    scroller.addEventListener('scroll', schedule, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    ro?.observe(scroller);
    ro?.observe(deck);
    // La línea del coach crece mientras escribe: el desvanecido la sigue.
    const featured = scroller.querySelector('.card.coach.is-featured');
    if (featured) ro?.observe(featured);
    return () => {
      scroller.removeEventListener('scroll', schedule);
      ro?.disconnect();
      if (raf) cancelAnimationFrame(raf);
      scroller.style.removeProperty('--asst-fade');
    };
  });
}

/** Escuchar · el micrófono grande · Seguir (la botonera de la maqueta 2). */
function Controls({
  recorder,
  holdDisabled,
  active,
  failed,
  accepted,
  playing,
  copy,
  onListen,
  onFollow,
  onRepeat,
  reopenedRef,
}: {
  recorder: HoldRecorder | undefined;
  holdDisabled: boolean;
  /** La frase está para decir (Escuchar y Seguir sirven). */
  active: boolean;
  failed: boolean;
  accepted: boolean;
  playing: boolean;
  copy: (typeof ASSISTED_COPY)['es'];
  onListen: () => void;
  onFollow: () => void;
  onRepeat: () => void;
  reopenedRef?: RefObject<boolean>;
}) {
  const recording = recorder?.recording === true;
  const sideOff = !active || recording;
  return (
    <div className="asst-ctrl">
      <button
        type="button"
        className={`asst-side-btn ${playing ? 'is-playing' : ''}`}
        disabled={sideOff}
        onClick={onListen}
      >
        <i>
          <IconSpeaker size={19} />
        </i>
        {playing ? copy.card.listening2 : copy.card.listen}
      </button>
      <div className="asst-micwrap">
        {recorder ? (
          <DeckMic
            recorder={recorder}
            disabled={holdDisabled || accepted}
            accepted={accepted}
            label={copy.card.said}
            {...(reopenedRef ? { reopenedRef } : {})}
          />
        ) : (
          <span className="asst-mic is-off" aria-hidden />
        )}
        {failed ? (
          <button type="button" className="asst-again" onClick={onRepeat}>
            {copy.card.repeat}
          </button>
        ) : (
          <div className="asst-miclabel" aria-hidden="true">
            {accepted
              ? ' '
              : recording
                ? recorder?.locked
                  ? copy.card.micLocked
                  : copy.card.micHolding
                : copy.card.micHold}
          </div>
        )}
      </div>
      <button type="button" className="asst-side-btn" disabled={sideOff} onClick={onFollow}>
        <i>
          <SkipIcon />
        </i>
        {copy.card.follow}
      </button>
    </div>
  );
}

/** Apretado al menos esto, es «mantener» (el mismo umbral de tap que `useHoldGesture`). */
const HOLD_MS = 450;

/**
 * El micrófono de la hoja, con el gesto del composer (`useHoldGesture`):
 * mantener = soltar manda; un toque lo deja grabando (fijado) y, fijado, otro
 * toque manda (en el composer eso lo hacía el panel de enviar, que acá no está).
 *
 * Abierto por «Decila una vez más» (`reopenedRef`), el primer apretón es el de la
 * primera vez (Emmanuel, 22/10, card #603): no lo corta. Si lo mantiene, soltar
 * manda; si es un toque, sigue grabando y el próximo toque manda.
 */
function DeckMic({
  recorder,
  disabled,
  accepted,
  label,
  reopenedRef,
}: {
  recorder: HoldRecorder;
  disabled: boolean;
  accepted: boolean;
  label: string;
  reopenedRef?: RefObject<boolean>;
}) {
  const { handlers } = useHoldGesture(recorder, disabled);
  const recording = recorder.recording;
  const lockedTapRef = useRef(false);
  /** Cuándo apretó el micrófono que abrió «Decila una vez más» (-1 = no). */
  const reopenedDownRef = useRef(-1);
  useEffect(() => {
    if (!recording && reopenedRef) reopenedRef.current = false;
  }, [recording, reopenedRef]);
  const onPointerDown = (e: ReactPointerEvent): void => {
    if (disabled) return;
    if (recorder.recording && recorder.locked && reopenedRef?.current) {
      e.preventDefault();
      reopenedRef.current = false;
      reopenedDownRef.current = performance.now();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      return;
    }
    if (recorder.recording && recorder.locked) {
      e.preventDefault();
      lockedTapRef.current = true;
      recorder.send();
      return;
    }
    lockedTapRef.current = false;
    handlers.onPointerDown(e);
  };
  const end = (): void => {
    if (reopenedDownRef.current >= 0) {
      const held = performance.now() - reopenedDownRef.current;
      reopenedDownRef.current = -1;
      if (held >= HOLD_MS) recorder.send();
      return;
    }
    if (lockedTapRef.current) {
      lockedTapRef.current = false;
      return;
    }
    handlers.onPointerUp();
  };
  return (
    <button
      type="button"
      className={`asst-mic ${recording ? 'is-rec' : ''} ${accepted ? 'is-done' : ''} ${disabled && !accepted ? 'is-off' : ''}`}
      aria-label={accepted ? label : 'Micrófono'}
      disabled={disabled && !recording}
      onPointerDown={onPointerDown}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onContextMenu={handlers.onContextMenu}
    >
      {accepted ? (
        <IconCheck size={34} />
      ) : recording ? (
        <span className="asst-recwave" aria-hidden="true">
          {[0.55, 0.9, 0.4, 1, 0.65].map((h, k) => (
            <i key={k} style={{ ['--h' as string]: h }} />
          ))}
        </span>
      ) : (
        <IconMicSolid size={34} />
      )}
    </button>
  );
}

function SkipIcon() {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 5l9 7-9 7V5z" />
      <path d="M18 5v14" />
    </svg>
  );
}

/** «Escuchar» la frase con la voz del coach (el mismo preview que la bombita). */
function useListen(
  voiceId: string,
  onDuck: ((ducked: boolean) => void) | undefined,
): { playing: boolean; play: (text: string) => Promise<void>; stop: () => void } {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const duckRef = useRef(onDuck);
  duckRef.current = onDuck;
  useEffect(
    () => () => {
      audioRef.current?.pause();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      duckRef.current?.(false);
    },
    [],
  );
  const play = async (text: string): Promise<void> => {
    if (playing) return;
    setPlaying(true);
    duckRef.current?.(true);
    try {
      const blob = await previewVoice(voiceId, text);
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      const audio = new Audio(url);
      bindMediaAudioFocus(audio, 'speak:assisted');
      audioRef.current = audio;
      const release = (): void => {
        setPlaying(false);
        duckRef.current?.(false);
        URL.revokeObjectURL(url);
        if (urlRef.current === url) urlRef.current = null;
      };
      audio.onended = release;
      audio.onerror = release;
      await audio.play();
    } catch (err) {
      console.warn('[assisted] listen failed', err);
      setPlaying(false);
      duckRef.current?.(false);
    }
  };
  const stop = (): void => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current = null;
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlaying(false);
    duckRef.current?.(false);
  };
  return { playing, play, stop };
}
